import { useState } from 'react';
import axios from 'axios';
import { calculateEndDate, formatDecimalToYearsMonths } from '../../components/componentFormUtils';
import {
  computeAssemblyComparison,
  computeComponentSelfComparison,
  findAirframeAssembly,
  parseValueByUnit,
  formatValueByUnit
} from '../../components/tsrComparisonUtils';
import { generateAssemblyCode } from '../../components/componentFormUtils';
import { getLiveRawForTsr, liveTsrContextFromRecord } from '../../../utils/liveTlbUsageMapping';

/**
 * Custom hook for CSV export functionality
 * Handles exporting assemblies and components to CSV format
 */
export const useCSVExport = (selectedAircraft, showArchived, currentUsageTotals = null, effectiveTlbUsageMapping = null) => {
  const [exporting, setExporting] = useState(false);

  const formatSignedValueByUnit = (numericValue, unit) => {
    if (numericValue == null || Number.isNaN(numericValue)) return '';
    const trimmedUnit = String(unit || '').trim();
    const isNegative = Number(numericValue) < 0;
    const signPrefix = isNegative ? '-' : '';

    if (trimmedUnit === 'Hours') {
      const absMinutes = Math.abs(Math.floor(numericValue));
      const hours = Math.floor(absMinutes / 60);
      const minutes = absMinutes % 60;
      return `${signPrefix}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    if (trimmedUnit === 'Landings') {
      return `${signPrefix}${Math.floor(Math.abs(numericValue)).toString()}`;
    }

    if (trimmedUnit === 'Years') {
      return `${signPrefix}${formatDecimalToYearsMonths(Math.abs(numericValue))}`;
    }

    if (trimmedUnit === 'Calendar Days') {
      return `${signPrefix}${Math.floor(Math.abs(numericValue)).toString()}`;
    }

    if (trimmedUnit === 'Cycles') {
      const v = Math.round(Math.abs(numericValue) * 100) / 100;
      return `${signPrefix}${v}`;
    }

    return `${numericValue}`;
  };
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If value contains comma, newline, or quote, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleString();
    } catch (e) {
      return String(date);
    }
  };

  const formatArray = (arr, formatter) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return '';
    return arr.map(item => {
      if (formatter) {
        return formatter(item);
      }
      if (typeof item === 'object' && item !== null) {
        return JSON.stringify(item);
      }
      return String(item);
    }).join('; ');
  };

  /** Get Limit, Elapsed, Remaining, Start Date, As On Date, End Date and value-at-installation fields for a time-since record */
  const getTimeSinceRecordCells = (record) => {
    const endDate = calculateEndDate(record.asOnDate, record.remainingTime);
    const limitStr = [record.limit?.value ?? 'N/A', record.limit?.unit].filter(Boolean).join(' ') || 'N/A';
    const elapsedStr = [record.elapsedTime?.value, record.elapsedTime?.unit].filter(Boolean).join(' ') || '';
    const remainingStr = [record.remainingTime?.value, record.remainingTime?.unit].filter(Boolean).join(' ') || '';
    const airframeValStr = [record.airframeValueAtInstallation?.value, record.airframeValueAtInstallation?.unit].filter(Boolean).join(' ') || (record.installedValue?.value != null ? [record.installedValue.value, record.installedValue.unit].filter(Boolean).join(' ') : '') || '';
    const parentValStr = [record.parentAssemblyValueAtInstallation?.value, record.parentAssemblyValueAtInstallation?.unit].filter(Boolean).join(' ') || '';
    const selfValStr = [record.assemblySelfValueAtInstallation?.value, record.assemblySelfValueAtInstallation?.unit].filter(Boolean).join(' ') || '';
    return {
      type: record.type || '',
      label: record.label || '',
      limit: limitStr,
      elapsed: elapsedStr,
      airframeValueAtInstallation: airframeValStr,
      parentAssemblyValueAtInstallation: parentValStr,
      thisAssemblyValueAtInstallation: selfValStr,
      remaining: remainingStr,
      startDate: formatDate(record.startDate),
      asOnDate: formatDate(record.asOnDate),
      endDate: formatDate(endDate)
    };
  };

  const convertAssembliesToCSV = (assembliesData) => {
    if (!assembliesData || assembliesData.length === 0) {
      return '';
    }

    const headers = [
      'Aircraft Registration',
      'Assembly Type',
      'ATA Chapter Code',
      'ATA Chapter Name',
      'Manufacturer',
      'Model',
      'Serial Number',
      'Position',
      'Installed On Date',
      'Installation Remarks',
      'Work Order Number',
      'As-received evidence from',
      'Initial load',
      'License',
      'Place of Installation',
      'Notes',
      'Revision Number',
      'Book Number',
      'Source Document',
      'Page Number',
      'Status',
      'Created By',
      'Created At',
      'Submitted By',
      'Submitted At',
      'Approved By',
      'Approved At',
      'Rejected By',
      'Rejected At',
      'Rejection Reason',
      'Is Active',
      'Archived Reason',
      'Archived By',
      'Archived At',
      'Updated At',
      'Values At Installation',
      'Time Since Type',
      'Time Since Label',
      'Limit',
      'Elapsed',
      'Airframe Value at Installation',
      "Parent Assembly's Value at Installation",
      "This Assembly's Value at Installation",
      'Remaining',
      'Start Date',
      'As On Date',
      'End Date',
      'Since New Values'
    ];

    const formatValuesAtInstallation = (arr) =>
      formatArray(arr, (val) => `${val.columnName || ''}: Assembly=${val.assemblyValue || ''}, Airframe=${val.airframeValue || ''}`);

    const baseAssemblyCells = (assembly) => {
      const valuesAtInstallationStr = formatValuesAtInstallation(assembly.valuesAtInstallation);
      const customValuesStr = formatValuesAtInstallation(assembly.customValuesAtInstallation);
      const valuesAtInstallation = [valuesAtInstallationStr, customValuesStr].filter(Boolean).join('; ');
      const sinceNewValues = formatArray(assembly.sinceNewValues, (val) => {
        return `Period=${val.period || ''}: Assembly=${val.assemblyValue || ''}, Airframe=${val.airframeValue || ''}`;
      });
      return [
        escapeCSV(assembly.aircraftId?.registration || ''),
        escapeCSV(assembly.type?.name || assembly.typeName || ''),
        escapeCSV(assembly.ataChapterId?.code || ''),
        escapeCSV(assembly.ataChapterId?.name || ''),
        escapeCSV(assembly.manufacturerId?.manufacturerName?.name || ''),
        escapeCSV(assembly.modelId?.name || ''),
        escapeCSV(assembly.serialNumber || ''),
        escapeCSV(assembly.position || ''),
        escapeCSV(formatDate(assembly.installedOnDate)),
        escapeCSV(assembly.installationRemarks || ''),
        escapeCSV(assembly.workOrderNumber || ''),
        escapeCSV(assembly.asReceivedEvidenceFrom || ''),
        escapeCSV(assembly.isInitialLoad ? 'Yes' : 'No'),
        escapeCSV(assembly.licenseId ? (assembly.licenseId.licenseNumber || assembly.licenseId.employeeName || '') : ''),
        escapeCSV(assembly.placeOfInstallation || ''),
        escapeCSV(assembly.notes || ''),
        escapeCSV(assembly.revisionNumber || ''),
        escapeCSV(assembly.bookNumber || ''),
        escapeCSV(assembly.sourceDocument || ''),
        escapeCSV(assembly.pageNumber || ''),
        escapeCSV(assembly.status || ''),
        escapeCSV(assembly.createdBy ? `${assembly.createdBy.firstName || ''} ${assembly.createdBy.lastName || ''}`.trim() || assembly.createdBy.email || '' : ''),
        escapeCSV(formatDate(assembly.createdAt)),
        escapeCSV(assembly.submittedBy ? `${assembly.submittedBy.firstName || ''} ${assembly.submittedBy.lastName || ''}`.trim() || assembly.submittedBy.email || '' : ''),
        escapeCSV(formatDate(assembly.submittedAt)),
        escapeCSV(assembly.approvedBy ? `${assembly.approvedBy.firstName || ''} ${assembly.approvedBy.lastName || ''}`.trim() || assembly.approvedBy.email || '' : ''),
        escapeCSV(formatDate(assembly.approvedAt)),
        escapeCSV(assembly.rejectedBy ? `${assembly.rejectedBy.firstName || ''} ${assembly.rejectedBy.lastName || ''}`.trim() || assembly.rejectedBy.email || '' : ''),
        escapeCSV(formatDate(assembly.rejectedAt)),
        escapeCSV(assembly.rejectionReason || ''),
        escapeCSV(assembly.isActive ? 'Yes' : 'No'),
        escapeCSV(assembly.archivedReason || ''),
        escapeCSV(assembly.archivedBy ? (typeof assembly.archivedBy === 'object' ? `${assembly.archivedBy.firstName || ''} ${assembly.archivedBy.lastName || ''}`.trim() || assembly.archivedBy.email || '' : '') : ''),
        escapeCSV(formatDate(assembly.archivedAt)),
        escapeCSV(formatDate(assembly.updatedAt)),
        escapeCSV(valuesAtInstallation)
      ];
    };

    const rows = [];
    assembliesData.forEach(assembly => {
      const base = baseAssemblyCells(assembly);
      const sinceNewValuesStr = formatArray(assembly.sinceNewValues, (val) => {
        return `Period=${val.period || ''}: Assembly=${val.assemblyValue || ''}, Airframe=${val.airframeValue || ''}`;
      });
      const records = assembly.timeSinceRecords && Array.isArray(assembly.timeSinceRecords) && assembly.timeSinceRecords.length > 0
        ? assembly.timeSinceRecords
        : [{}];
      records.forEach((record) => {
        const cells = record.type != null ? getTimeSinceRecordCells(record) : { type: '', label: '', limit: '', elapsed: '', airframeValueAtInstallation: '', parentAssemblyValueAtInstallation: '', thisAssemblyValueAtInstallation: '', remaining: '', startDate: '', asOnDate: '', endDate: '' };
        rows.push([
          ...base,
          escapeCSV(cells.type),
          escapeCSV(cells.label),
          escapeCSV(cells.limit),
          escapeCSV(cells.elapsed),
          escapeCSV(cells.airframeValueAtInstallation),
          escapeCSV(cells.parentAssemblyValueAtInstallation),
          escapeCSV(cells.thisAssemblyValueAtInstallation),
          escapeCSV(cells.remaining),
          escapeCSV(cells.startDate),
          escapeCSV(cells.asOnDate),
          escapeCSV(cells.endDate),
          escapeCSV(sinceNewValuesStr)
        ]);
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  };

  const convertComponentsToCSV = (componentsData, assembliesData) => {
    if (!componentsData || componentsData.length === 0) {
      return '';
    }

    const headers = [
      'Aircraft Registration',
      'Assembly Type',
      'Assembly Serial Number',
      'Component Code',
      'Part Number',
      'Description',
      'ATA Chapter Code',
      'ATA Chapter Name',
      'Serial Number',
      'Position',
      'Manufacturer',
      'Threshold Value',
      'Threshold Value Unit',
      'Frequency',
      'Frequency Unit',
      'Extension',
      'Extension Unit',
      'Frequency Hours',
      'Frequency Cycles',
      'Monitor Type',
      'Elapsed Time',
      'Elapsed Time Unit',
      'Remaining Time',
      'Remaining Time Unit',
      'Due As Of',
      'Installed On Date',
      'Installation Remarks',
      'Work Order Number',
      'As-received evidence from',
      'Initial load',
      'Agency',
      'License',
      'Place of Installation',
      'Notes',
      'Revision Number',
      'Book Number',
      'Source Document',
      'Page Number',
      'Status',
      'Created By',
      'Created At',
      'Submitted By',
      'Submitted At',
      'Approved By',
      'Approved At',
      'Rejected By',
      'Rejected At',
      'Rejection Reason',
      'Is Active',
      'Archived Reason',
      'Archived By',
      'Archived At',
      'Updated At',
      'Time Since Type',
      'Time Since Label',
      'Limit',
      'Elapsed',
      'Remaining',
      'Start Date',
      'As On Date',
      'End Date',
      'Assembly Value at Install',
      'Assembly Value at Due',
      'Component Value at Install',
      'Component Value at Due',
      'Airframe Value at Install',
      'Airframe Value at Due',
      'Remaining until Due (Live)',
      'Airframe Assembly ID',
      'Since New Values'
    ];

    const baseComponentCells = (component) => {
      return [
        escapeCSV(component.aircraftId?.registration || ''),
        escapeCSV(component.assemblyId?.typeName || ''),
        escapeCSV(component.assemblyId?.serialNumber || ''),
        escapeCSV(component.code || ''),
        escapeCSV(component.partNumber || ''),
        escapeCSV(component.description || ''),
        escapeCSV(component.ataChapterId?.code || ''),
        escapeCSV(component.ataChapterId?.name || ''),
        escapeCSV(component.serialNumber || ''),
        escapeCSV(component.position || ''),
        escapeCSV(component.manufacturerId?.manufacturerName?.name || ''),
        escapeCSV(component.thresholdValue || ''),
        escapeCSV(component.thresholdValueUnit || ''),
        escapeCSV(component.frequency || ''),
        escapeCSV(component.frequencyUnit || ''),
        escapeCSV(component.extension || ''),
        escapeCSV(component.extensionUnit || ''),
        escapeCSV(component.frequencyHours || ''),
        escapeCSV(component.frequencyCycles || ''),
        escapeCSV(component.monitorTypeId ? (component.monitorTypeId.name || component.monitorTypeId.code || '') : ''),
        escapeCSV(component.elapsedTime || ''),
        escapeCSV(component.elapsedTimeUnit || ''),
        escapeCSV(component.remainingTime || ''),
        escapeCSV(component.remainingTimeUnit || ''),
        escapeCSV(component.dueAsOf || ''),
        escapeCSV(formatDate(component.installedOnDate)),
        escapeCSV(component.installationRemarks || ''),
        escapeCSV(component.workOrderNumber || ''),
        escapeCSV(component.asReceivedEvidenceFrom || ''),
        escapeCSV(component.isInitialLoad ? 'Yes' : 'No'),
        escapeCSV(component.agencyId?.name || ''),
        escapeCSV(component.licenseId ? (component.licenseId.licenseNumber || component.licenseId.employeeName || '') : ''),
        escapeCSV(component.placeOfInstallation || ''),
        escapeCSV(component.notes || ''),
        escapeCSV(component.revisionNumber || ''),
        escapeCSV(component.bookNumber || ''),
        escapeCSV(component.sourceDocument || ''),
        escapeCSV(component.pageNumber || ''),
        escapeCSV(component.status || ''),
        escapeCSV(component.createdBy ? `${component.createdBy.firstName || ''} ${component.createdBy.lastName || ''}`.trim() || component.createdBy.email || '' : ''),
        escapeCSV(formatDate(component.createdAt)),
        escapeCSV(component.submittedBy ? `${component.submittedBy.firstName || ''} ${component.submittedBy.lastName || ''}`.trim() || component.submittedBy.email || '' : ''),
        escapeCSV(formatDate(component.submittedAt)),
        escapeCSV(component.approvedBy ? `${component.approvedBy.firstName || ''} ${component.approvedBy.lastName || ''}`.trim() || component.approvedBy.email || '' : ''),
        escapeCSV(formatDate(component.approvedAt)),
        escapeCSV(component.rejectedBy ? `${component.rejectedBy.firstName || ''} ${component.rejectedBy.lastName || ''}`.trim() || component.rejectedBy.email || '' : ''),
        escapeCSV(formatDate(component.rejectedAt)),
        escapeCSV(component.rejectionReason || ''),
        escapeCSV(component.isActive ? 'Yes' : 'No'),
        escapeCSV(component.archivedReason || ''),
        escapeCSV(component.archivedBy ? (typeof component.archivedBy === 'object' ? `${component.archivedBy.firstName || ''} ${component.archivedBy.lastName || ''}`.trim() || component.archivedBy.email || '' : '') : ''),
        escapeCSV(formatDate(component.archivedAt)),
        escapeCSV(formatDate(component.updatedAt))
      ];
    };

    const { assembly: airframeAssembly } = findAirframeAssembly(assembliesData || []);
    const airframeRecords = airframeAssembly?.timeSinceRecords || [];
    const airframeAssemblyId = airframeAssembly ? (generateAssemblyCode(airframeAssembly) || airframeAssembly._id || '') : '';

    const rows = [];
    componentsData.forEach(component => {
      const base = baseComponentCells(component);
      const assemblyId = component.assemblyId?._id || component.assemblyId;
      const parentAssembly = (assembliesData || []).find((a) => {
        const idStr = a._id ? String(a._id) : null;
        const cmpStr = assemblyId ? String(assemblyId) : null;
        return idStr && cmpStr && idStr === cmpStr;
      });
      const assemblyRecords = parentAssembly?.timeSinceRecords || [];
      const sinceNewValuesStr = formatArray(component.sinceNewValues, (val) => {
        return `Period=${val.period || ''}: Component=${val.componentValue || ''}, Assembly=${val.assemblyValue || ''}, Values At=${val.valuesAt || ''}`;
      });
      const records = component.timeSinceRecords && Array.isArray(component.timeSinceRecords) && component.timeSinceRecords.length > 0
        ? component.timeSinceRecords
        : [{}];
      records.forEach((record) => {
        const cells = record.type != null
          ? getTimeSinceRecordCells(record)
          : { type: '', label: '', limit: '', elapsed: '', remaining: '', startDate: '', asOnDate: '', endDate: '' };

        let assemblyInstall = '';
        let assemblyDue = '';
        let componentInstall = '';
        let componentDue = '';
        let airframeInstall = '';
        let airframeDue = '';
        let airframeRemaining = '';

        if (record.type != null) {
          const assemblyComparison = computeAssemblyComparison(record, assemblyRecords);
          const componentComparison = computeComponentSelfComparison(record);
          const airframeComparison = computeAssemblyComparison(record, airframeRecords);

          // Unit and remaining used for snapshot-based calculations
          const unit =
            record.elapsedTime?.unit ||
            record.limit?.unit ||
            record.remainingTime?.unit ||
            '';
          const remainingStr = record.remainingTime?.value;

          // Assembly: prefer Parent Assembly's Value at Installation + Remaining, else comparison
          const parentSnapshotStr = record.parentAssemblyValueAtInstallation?.value;
          if (
            parentSnapshotStr != null &&
            String(parentSnapshotStr).trim() !== '' &&
            remainingStr != null &&
            String(remainingStr).trim() !== '' &&
            unit
          ) {
            try {
              const installedNumeric = parseValueByUnit(parentSnapshotStr, unit);
              const remainingNumeric = parseValueByUnit(remainingStr, unit);
              const dueNumeric = installedNumeric + remainingNumeric;
              assemblyInstall = formatValueByUnit(installedNumeric, unit) || '';
              assemblyDue = formatValueByUnit(dueNumeric, unit) || '';
            } catch (e) {
              assemblyInstall = '';
              assemblyDue = '';
            }
          }
          if (!assemblyInstall && assemblyComparison.installedAtAssembly) {
            assemblyInstall = assemblyComparison.installedAtAssembly || '';
          }
          if (!assemblyDue && assemblyComparison.dueAtAssembly) {
            assemblyDue = assemblyComparison.dueAtAssembly || '';
          }

          // Component self-comparison: already uses snapshot + remaining or limit
          componentInstall = componentComparison.installedAtComponent || '';
          componentDue = componentComparison.dueAtComponent || '';

          // Airframe: prefer Airframe Value at Installation + Remaining, else comparison
          const airframeSnapshotStr = record.airframeValueAtInstallation?.value;
          if (
            airframeSnapshotStr != null &&
            String(airframeSnapshotStr).trim() !== '' &&
            unit
          ) {
            try {
              const installedNumeric = parseValueByUnit(airframeSnapshotStr, unit);
              airframeInstall = formatValueByUnit(installedNumeric, unit) || '';
            } catch (e) {
              airframeInstall = '';
            }
          }
          if (!airframeInstall && airframeComparison.installedAtAssembly) {
            airframeInstall = airframeComparison.installedAtAssembly || '';
          }

          const limitRaw = record?.limit?.value;
          const { raw: liveRaw } = getLiveRawForTsr(
            currentUsageTotals?.values,
            effectiveTlbUsageMapping,
            liveTsrContextFromRecord(record, { assemblyRole: 'Airframe' })
          );
          const componentElapsedAtInstallRaw = record?.elapsedTime?.value ?? record?.installedValue?.value;
          if (
            limitRaw != null &&
            String(limitRaw).trim() !== '' &&
            liveRaw != null &&
            airframeSnapshotStr != null &&
            String(airframeSnapshotStr).trim() !== '' &&
            componentElapsedAtInstallRaw != null &&
            String(componentElapsedAtInstallRaw).trim() !== ''
          ) {
            try {
              const airframeLiveNumeric = parseValueByUnit(liveRaw, unit);
              const limitNumeric = parseValueByUnit(limitRaw, unit);
              const airframeAtInstallNumeric = parseValueByUnit(airframeSnapshotStr, unit);
              const componentElapsedAtInstallNumeric = parseValueByUnit(componentElapsedAtInstallRaw, unit);

              const dueNumeric = airframeAtInstallNumeric + (limitNumeric - componentElapsedAtInstallNumeric);
              const remainingNumeric = dueNumeric - airframeLiveNumeric;

              airframeDue = formatValueByUnit(dueNumeric, unit) || '';
              airframeRemaining = formatSignedValueByUnit(remainingNumeric, unit) || '';
            } catch (e) {
              airframeDue = '';
              airframeRemaining = '';
            }
          }
        }
        rows.push([
          ...base,
          escapeCSV(cells.type),
          escapeCSV(cells.label),
          escapeCSV(cells.limit),
          escapeCSV(cells.elapsed),
          escapeCSV(cells.remaining),
          escapeCSV(cells.startDate),
          escapeCSV(cells.asOnDate),
          escapeCSV(cells.endDate),
          escapeCSV(assemblyInstall),
          escapeCSV(assemblyDue),
          escapeCSV(componentInstall),
          escapeCSV(componentDue),
          escapeCSV(airframeInstall),
          escapeCSV(airframeDue),
          escapeCSV(airframeRemaining),
          escapeCSV(airframeAssemblyId),
          escapeCSV(sinceNewValuesStr)
        ]);
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  };

  const handleExportToCSV = async (options = {}) => {
    if (!selectedAircraft) {
      alert('Please select an aircraft first');
      return;
    }

    try {
      setExporting(true);
      
      // Fetch all assemblies and components for the selected aircraft
      const params = {
        aircraftId: selectedAircraft._id
      };
      
      const includeArchived = options.includeArchived ?? showArchived;
      if (!includeArchived) {
        params.isActive = 'true';
      }

      const [assembliesResponse, componentsResponse] = await Promise.all([
        axios.get('/api/assemblies', { params }),
        axios.get('/api/components', { params })
      ]);

      const allAssemblies = assembliesResponse.data || [];
      const allComponents = componentsResponse.data || [];

      if (allAssemblies.length === 0 && allComponents.length === 0) {
        alert('No assemblies or components found to export');
        setExporting(false);
        return;
      }

      // Generate timestamp for filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const aircraftReg = selectedAircraft.registration || 'aircraft';
      const target = options.target || 'both';

      // Export assemblies
      if ((target === 'both' || target === 'assemblies') && allAssemblies.length > 0) {
        const assembliesCSV = convertAssembliesToCSV(allAssemblies);
        const assembliesBlob = new Blob([assembliesCSV], { type: 'text/csv;charset=utf-8;' });
        const assembliesLink = document.createElement('a');
        const assembliesUrl = URL.createObjectURL(assembliesBlob);
        const assembliesFilename = `${aircraftReg}-assemblies-export-${timestamp}.csv`;
        
        assembliesLink.setAttribute('href', assembliesUrl);
        assembliesLink.setAttribute('download', assembliesFilename);
        assembliesLink.style.visibility = 'hidden';
        document.body.appendChild(assembliesLink);
        assembliesLink.click();
        document.body.removeChild(assembliesLink);
        URL.revokeObjectURL(assembliesUrl);
      }

      // Export components
      if ((target === 'both' || target === 'components') && allComponents.length > 0) {
        const componentsCSV = convertComponentsToCSV(allComponents, allAssemblies);
        const componentsBlob = new Blob([componentsCSV], { type: 'text/csv;charset=utf-8;' });
        const componentsLink = document.createElement('a');
        const componentsUrl = URL.createObjectURL(componentsBlob);
        const componentsFilename = `${aircraftReg}-components-export-${timestamp}.csv`;
        
        componentsLink.setAttribute('href', componentsUrl);
        componentsLink.setAttribute('download', componentsFilename);
        componentsLink.style.visibility = 'hidden';
        document.body.appendChild(componentsLink);
        componentsLink.click();
        document.body.removeChild(componentsLink);
        URL.revokeObjectURL(componentsUrl);
      }

      if (target === 'assemblies') {
        alert(`Successfully exported ${allAssemblies.length} assemblies to CSV`);
      } else if (target === 'components') {
        alert(`Successfully exported ${allComponents.length} components to CSV`);
      } else {
        alert(`Successfully exported ${allAssemblies.length} assemblies and ${allComponents.length} components to CSV`);
      }
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Error exporting data to CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return {
    handleExportToCSV,
    exporting
  };
};
